import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "@/lib/api";
import { Users } from "lucide-react";

// Fix default icon paths (Leaflet + Webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom pin: circle with count, viola/fucsia branding
function makePin(count) {
  const size = count >= 20 ? 44 : count >= 10 ? 38 : count >= 5 ? 32 : 28;
  const bg = count >= 20 ? "#EC49B0" : count >= 10 ? "#8B5CF6" : "#4C1D95";
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;background:${bg};color:#FFF6E5;border-radius:9999px;display:grid;place-items:center;font-weight:700;font-size:12px;border:2px solid #FFF6E5;box-shadow:0 4px 12px rgba(76,29,149,0.35);">${count}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, points]);
  return null;
}

export default function ReteMap({ onCitySelect }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    http.get("/rete/map").then((r) => setPoints(r.data)).finally(() => setLoading(false));
    // Delay mounting the map to avoid React StrictMode double-init on Leaflet
    const t = setTimeout(() => setMounted(true), 0);
    return () => clearTimeout(t);
  }, []);

  if (loading || !mounted) {
    return <div className="h-[500px] rounded-2xl bg-lilla/40 grid place-items-center text-viola/60 dark:bg-viola/30 dark:text-cream/60" data-testid="map-loading">Caricamento mappa…</div>;
  }

  const totalPros = points.reduce((s, p) => s + p.count, 0);
  const totalCities = points.length;

  return (
    <div className="rounded-2xl overflow-hidden border border-viola/10 dark:border-cream/10 bg-white dark:bg-viola/40" data-testid="rete-map">
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3 border-b border-viola/10 dark:border-cream/10">
        <p className="font-display text-lg dark:text-cream">Distribuzione geografica</p>
        <p className="text-xs uppercase tracking-widest text-viola/60 dark:text-cream/60">
          {totalCities} città · {totalPros} professionistə (multi-sede inclusə)
        </p>
      </div>
      <div style={{ height: 520 }}>
        <MapContainer
          center={[42.5, 12.5]}
          zoom={6}
          minZoom={5}
          maxZoom={18}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
          className="dark:bg-viola/60"
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={points} />
          <MarkerClusterGroup chunkedLoading maxClusterRadius={40}>
            {points.map((p) => (
              <Marker key={p.city} position={[p.lat, p.lon]} icon={makePin(p.count)}>
                <Popup>
                  <div className="min-w-[220px]" data-testid={`map-popup-${p.city}`}>
                    <p className="font-display text-lg font-bold text-viola">{p.city}</p>
                    <p className="text-xs text-viola/70 mb-2 flex items-center gap-1">
                      <Users size={11} /> {p.count} professionistə
                    </p>
                    <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                      {p.professionals.slice(0, 8).map((pro) => (
                        <li key={`${pro.cognome}-${pro.nome}`} className="text-viola/85">
                          • {pro.nome} {pro.cognome}
                          {pro.qualifica && <span className="text-viola/50"> — {pro.qualifica.split(",")[0]}</span>}
                        </li>
                      ))}
                      {p.professionals.length > 8 && (
                        <li className="text-viola/60 italic">…e altrə {p.professionals.length - 8}</li>
                      )}
                    </ul>
                    {onCitySelect && (
                      <button
                        onClick={() => onCitySelect(p.city)}
                        className="mt-3 w-full rounded-full bg-fucsia text-white text-xs font-semibold py-1.5 hover:bg-viola transition-colors"
                        data-testid={`map-filter-city-${p.city}`}
                      >
                        Filtra {p.count} scheda{p.count > 1 ? "e" : ""}
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
